/**
 * ViewPortrait — read-only summary of the onboarding survey answers, split into
 * the journey questions and the readiness ratings (mobile PortraitView parity).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSurvey } from '@wlb/shared/context/SurveyContext';
import useHydrateSurvey from '@wlb/shared/hooks/useHydrateSurvey';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { ChevronLeftIcon, PencilIcon } from '../components/ui/icons';
import {
  buildJourneyAnswers,
  buildReadinessRatings,
  isPortraitEmpty,
} from '../features/profile/portraitAnswers';
import { withEditFlag } from '../features/profile/portraitEdit';
import { ProfileCardSkeleton } from '../features/profile/ProfileSkeletons';
import '../features/profile/profile.css';

type PortraitTab = 'journey' | 'readiness';

const MAX_RATING = 5;

export function ViewPortrait() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { surveyData } = useSurvey();
  const [activeKey, setActiveKey] = useState<PortraitTab>('journey');
  const { loading } = useHydrateSurvey();

  const journey = useMemo(() => buildJourneyAnswers(surveyData, t), [surveyData, t]);
  const readiness = useMemo(() => buildReadinessRatings(surveyData, t), [surveyData, t]);
  const empty = isPortraitEmpty(surveyData);

  /** Journey answers start at q1; the readiness sliders are the last question. */
  const editPath = withEditFlag(activeKey === 'journey' || empty ? '/survey/q1' : '/survey/q9', true);

  const items = [
    { key: 'journey' as const, label: t('profile.portrait.journeyTab') },
    { key: 'readiness' as const, label: t('profile.portrait.readinessTab') },
  ];

  return (
    <section className="profile-page" aria-label={t('profile.header.viewPortrait')}>
      <div className="profile-topbar">
        <button
          type="button"
          className="profile-topbar__back"
          aria-label={t('profile.accessibility.back')}
          onClick={() => navigate('/profile')}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <h1 className="profile-topbar__title">{t('profile.header.viewPortrait')}</h1>
      </div>

      <section className="portrait-card">
        <div className="portrait-card__copy">
          <h2 className="portrait-card__heading">{t('profile.portrait.title')}</h2>
          <p className="portrait-card__subtitle">{t('profile.portrait.subtitle')}</p>
        </div>
        {!empty && (
          <Button
            size="sm"
            variant="outline"
            className="portrait-card__edit"
            onClick={() => navigate(editPath)}
          >
            <PencilIcon size={16} />
            {t(activeKey === 'journey' ? 'profile.buttons.editJourney' : 'profile.buttons.editReadiness')}
          </Button>
        )}
      </section>

      {loading ? (
        <ProfileCardSkeleton />
      ) : empty ? (
        <div className="profile-restricted">
          <p className="profile-restricted__message">{t('profile.portrait.empty')}</p>
          <Button size="sm" onClick={() => navigate(editPath)}>
            {t('profile.portrait.startSurvey')}
          </Button>
        </div>
      ) : (
        <section className="portrait-summary">
          <Tabs
            items={items}
            activeKey={activeKey}
            onChange={setActiveKey}
            label={t('profile.header.viewPortrait')}
            idPrefix="portrait-tab"
            className="portrait-tabs"
          />

          <div
            role="tabpanel"
            id={`portrait-tab-panel-${activeKey}`}
            aria-labelledby={`portrait-tab-${activeKey}`}
            className="portrait-list"
          >
            {activeKey === 'journey'
              ? (
                  <dl className="portrait-list__rows">
                    {journey.map(answer => (
                      <div className="portrait-item" key={answer.key}>
                        <dt className="portrait-item__question">{answer.question}</dt>
                        <dd className="portrait-item__answer">{answer.answer}</dd>
                      </div>
                    ))}
                  </dl>
                )
              : (
                  <dl className="portrait-list__rows">
                    {readiness.map(rating => {
                      const ratingLabel = t('survey.accessibility.starRating', {
                        rating: rating.value,
                        total: MAX_RATING,
                      });
                      return (
                        <div className="portrait-item portrait-item--rating" key={rating.key}>
                          <dt className="portrait-item__question">{rating.question}</dt>
                          <dd className="portrait-rating">
                            <div
                              className="portrait-rating__meter"
                              role="img"
                              aria-label={ratingLabel}
                            >
                              {Array.from({ length: MAX_RATING }, (_, meterIndex) => (
                                <span
                                  key={meterIndex}
                                  className={`portrait-rating__segment${
                                    meterIndex < rating.value
                                      ? ' portrait-rating__segment--active'
                                      : ''
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="portrait-rating__value">{ratingLabel}</span>
                            <div className="portrait-rating__labels">
                              <span>{rating.leftLabel}</span>
                              <span>{rating.rightLabel}</span>
                            </div>
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                )}
          </div>
        </section>
      )}
    </section>
  );
}
